import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@mental-mint/shared-ui';
import { EVENT_NAMES, reportConversionEvent, GetTodaysDate, logger } from '@mental-mint/shared-utils';
import { upsertLevelScoreInSaveData, LevelScore } from '@mental-mint/shared-utils';
import { CraftegoryGameState } from '../../Data/types';
import {
  initializeGameState,
  findCombination,
  combineTerm,
  masterTerm,
  isGameWon,
  getUnlockedNonSeedTermIds,
} from '../../Utils/gameLogic';
import { getThemeForDate, getCraftegoryLevelId } from '../../Utils/puzzleUtils';
import { loadCraftegory, saveCraftegory } from '../../Utils/saveManager';
import { ConstellationGrid } from '../../Components/ConstellationGrid/ConstellationGrid';
import { TermHighlight, TermHighlightState } from '../../Components/TermHighlight/TermHighlight';
import { NewCombinationPopup, NewCombinationPopupState } from '../../Components/NewCombinationPopup/NewCombinationPopup';
import { TermQuizPopup, TermQuizPopupState } from '../../Components/TermQuizPopup/TermQuizPopup';
import { CraftegoryPageId } from '../CraftegoryPageTypes';
import './PagePlay.css';

interface PagePlayProps {
  navigateToPage?: (pageId: string, navigationProps?: any) => void;
}

export const PagePlay: React.FC<PagePlayProps> = ({ navigateToPage }) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;

  const [gameState, setGameState] = useState<CraftegoryGameState | null>(null);
  const [combinationsCount, setCombinationsCount] = useState(0);
  const [hasTrackedFirstInteraction, setHasTrackedFirstInteraction] = useState(false);
  const [lastUnlockedTermId, setLastUnlockedTermId] = useState<string | null>(null);
  const [termHighlightState, setTermHighlightState] = useState<TermHighlightState | null>(null);
  const [newCombinationPopupState, setNewCombinationPopupState] = useState<NewCombinationPopupState | null>(null);
  const [termQuizPopupState, setTermQuizPopupState] = useState<TermQuizPopupState | null>(null);

  const popupIdRef = useRef(0);
  const pendingUnlockRef = useRef<{
    newState: CraftegoryGameState;
    newCombosCount: number;
    productTermId: string;
  } | null>(null);

  // Load game state on mount
  useEffect(() => {
    const load = async () => {
      try {
        const saveData = await loadCraftegory();
        const today = GetTodaysDate();
        const levelId = getCraftegoryLevelId(today);
        const theme = getThemeForDate(today);
        const score = saveData.levelScores[levelId];
        const savedUnlocked = score?.gameMetadata?.unlockedTermIds ?? [];
        const savedQuizResults = score?.gameMetadata?.quizResults ?? {};
        const savedCombinationsCount = score?.gameMetadata?.combinationsCount ?? 0;

        const state = initializeGameState(theme, savedUnlocked, savedQuizResults);
        setGameState(state);
        setCombinationsCount(savedCombinationsCount);

        reportConversionEvent(
          EVENT_NAMES.DISPLAY_DAILY_PUZZLES,
          { oncePerDay: true },
          'Craftegory puzzle displayed'
        );
      } catch (err) {
        logger.error('[Craftegory PagePlay] Failed to load game state:', err);
      }
    };
    load();
  }, []);

  // Save helper
  const persistState = useCallback(
    async (state: CraftegoryGameState, combos: number) => {
      try {
        const today = GetTodaysDate();
        const levelId = getCraftegoryLevelId(today);
        const theme = getThemeForDate(today);
        const won = isGameWon(state);
        const unlockedTermIds = getUnlockedNonSeedTermIds(state, theme.seedTermIds);

        // Merge quiz results from existing save data
        const existingSave = await loadCraftegory();
        const existingScore = existingSave.levelScores[levelId];
        const existingQuizResults = existingScore?.gameMetadata?.quizResults ?? {};
        const existingTotal = existingScore?.gameMetadata?.totalQuizzesCompleted ?? 0;

        // Build updated quizResults from current state
        const quizResults: Record<string, number> = { ...existingQuizResults };
        let totalQuizzesCompleted = existingTotal;
        state.terms.forEach((term) => {
          if (term.isMastered && !term.termId.match(/^(nature|reason|virtue|fist|rock|stick)$/) && !(term.termId in quizResults)) {
            quizResults[term.termId] = Date.now();
            totalQuizzesCompleted += 1;
          }
        });

        const score: LevelScore<any> = {
          levelId,
          isWon: won,
          gameMetadata: { unlockedTermIds, combinationsCount: combos, isComplete: won, quizResults, totalQuizzesCompleted },
        };
        const saveData = await loadCraftegory();
        const updated = upsertLevelScoreInSaveData(saveData, score);
        await saveCraftegory(updated);
      } catch (err) {
        logger.error('[Craftegory PagePlay] Failed to save:', err);
      }
    },
    []
  );

  const handleDragCombine = useCallback(
    (term1Id: string, term2Id: string) => {
      if (!gameState) return;

      const term1 = gameState.terms.get(term1Id);
      const term2 = gameState.terms.get(term2Id);
      if (!term1?.isMastered || !term2?.isMastered) return;

      const id = ++popupIdRef.current;
      const combo = findCombination(gameState.possibleCombinations, term1Id, term2Id);

      if (!combo) {
        setTermHighlightState({ id, type: 'failed', isNew: false });
        return;
      }

      const product = gameState.terms.get(combo.productTermId) ?? null;
      if (!product) return;

      if (product.isUnlocked) {
        // Already discovered — show highlight without "new" flag, no state change
        setTermHighlightState({ id, type: 'success', combination: combo, product, isNew: false });
        return;
      }

      // New combination!
      if (!hasTrackedFirstInteraction) {
        reportConversionEvent(
          EVENT_NAMES.INTERACT_WITH_DAILY_PUZZLES,
          { oncePerDay: true },
          'User started playing Craftegory'
        );
        setHasTrackedFirstInteraction(true);
      }

      const newState = combineTerm(gameState, term1Id, term2Id);
      if (!newState) return;

      const newCombosCount = combinationsCount + 1;
      // Defer state unlock — don't show the new term in the grid until after REVEAL
      pendingUnlockRef.current = {
        newState,
        newCombosCount,
        productTermId: combo.productTermId,
      };

      // Show combination popup first — grid update happens on Next
      setNewCombinationPopupState({
        id,
        term1: term1,
        term2: term2,
        combination: combo,
        product: { ...product, isUnlocked: true },
      });
    },
    [gameState, combinationsCount, hasTrackedFirstInteraction, persistState]
  );

  const handleTermHighlightDismiss = useCallback(() => {
    setTermHighlightState(null);
  }, []);

  const handleQuizPass = useCallback(() => {
    if (!termQuizPopupState || !gameState) return;
    const { term } = termQuizPopupState;
    const newState = masterTerm(gameState, term.termId);
    setGameState(newState);
    setTermQuizPopupState(null);
    persistState(newState, combinationsCount);

    if (isGameWon(newState)) {
      reportConversionEvent(
        EVENT_NAMES.COMPLETE_DAILY_PUZZLES,
        { oncePerDay: true },
        'Craftegory puzzle completed'
      );
      const today = GetTodaysDate();
      const theme = getThemeForDate(today);
      setTimeout(() => {
        navigateToPage?.(CraftegoryPageId.Victory, {
          combinationsCount,
          themeName: theme.name,
          totalTerms: newState.terms.size,
        });
      }, 600);
    }
  }, [termQuizPopupState, gameState, combinationsCount, persistState, navigateToPage]);

  const handleQuizDismiss = useCallback(() => {
    setTermQuizPopupState(null);
  }, []);

  const handleTermClick = useCallback((termId: string) => {
    if (!gameState) return;
    const term = gameState.terms.get(termId);
    if (!term?.isUnlocked) return;

    if (!term.isMastered) {
      const today = GetTodaysDate();
      const theme = getThemeForDate(today);
      const quizzes = theme.quizzes?.[termId];
      const quiz = quizzes?.[0];
      if (quiz) {
        const id = ++popupIdRef.current;
        setTermQuizPopupState({ id, term, quiz });
      }
      return;
    }

    const id = ++popupIdRef.current;
    setTermHighlightState({ id, type: 'success', product: term, isNew: false });
  }, [gameState]);

  const handleNewCombinationNext = useCallback(() => {
    if (!newCombinationPopupState) return;
    const { id, combination, product } = newCombinationPopupState;

    // Apply deferred game state unlock now that the term is being revealed
    if (pendingUnlockRef.current) {
      const { newCombosCount, productTermId } = pendingUnlockRef.current;
      let { newState } = pendingUnlockRef.current;

      // Check if a quiz is required for this term
      const today = GetTodaysDate();
      const theme = getThemeForDate(today);
      const hasQuiz = !!(theme.quizzes?.[productTermId]?.length);

      if (!hasQuiz) {
        // No quiz — auto-master the term immediately
        newState = masterTerm(newState, productTermId);
      }
      // If hasQuiz, term stays unmastered (blurred) until quiz is passed

      setCombinationsCount(newCombosCount);
      setGameState(newState);
      setLastUnlockedTermId(productTermId);
      persistState(newState, newCombosCount);

      if (isGameWon(newState)) {
        setLastUnlockedTermId(null);
        reportConversionEvent(
          EVENT_NAMES.COMPLETE_DAILY_PUZZLES,
          { oncePerDay: true },
          'Craftegory puzzle completed'
        );
        setTimeout(() => {
          navigateToPage?.(CraftegoryPageId.Victory, {
            combinationsCount: newCombosCount,
            themeName: theme.name,
            totalTerms: newState.terms.size,
          });
        }, 600);
      }

      pendingUnlockRef.current = null;
    }

    setNewCombinationPopupState(null);
    setTermHighlightState({ id, type: 'success', combination, product, isNew: true });
  }, [newCombinationPopupState, persistState, navigateToPage]);

  if (!gameState) {
    return (
      <div className="page-play__loading">
        <div className="page-play__spinner" />
      </div>
    );
  }

  return (
    <div className="page-play" style={{ '--color-primary': game.colorPrimary } as React.CSSProperties}>
      <ConstellationGrid
        categories={gameState.categories}
        categoryOrder={gameState.categoryOrder}
        terms={gameState.terms}
        possibleCombinations={gameState.possibleCombinations}
        onDragCombine={handleDragCombine}
        lastUnlockedTermId={lastUnlockedTermId}
        onTermClick={handleTermClick}
      />

      <NewCombinationPopup
        state={newCombinationPopupState}
        onNext={handleNewCombinationNext}
      />
      <TermQuizPopup
        state={termQuizPopupState}
        onPass={handleQuizPass}
        onDismiss={handleQuizDismiss}
      />
      <TermHighlight
        state={termHighlightState}
        onDismiss={handleTermHighlightDismiss}
      />
    </div>
  );
};
